import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCaja } from '../contexts/CajaContext';
import { getApiUrl } from '../config/apiConfig';
import { socket } from '../config/socket';
import clientPrinterService from '../services/clientPrinterService';
import './DetallePedido.css';

const DetallePedidoComponent = () => {
  const { pedidoId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const origenCaja = location.state?.from === 'caja';
  const { auth } = useAuth();
  const { caja } = useCaja();

  const [pedido, setPedido] = useState(null);
  const [detalles, setDetalles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [mostrarModalCancelar, setMostrarModalCancelar] = useState(false);
  const [mensajeExito, setMensajeExito] = useState('');
  const [mensajeError, setMensajeError] = useState('');
  const [mostrarModalCancelarProducto, setMostrarModalCancelarProducto] = useState(false);
  const [productoACancelar, setProductoACancelar] = useState(null);
  const [motivoCancelacionProducto, setMotivoCancelacionProducto] = useState('');
  const [actualizandoEstado, setActualizandoEstado] = useState(false);
  const [menuEstadoId, setMenuEstadoId] = useState(null); // ID del producto con menú de estado abierto

  // Estados definidos para el flujo de trabajo
  const estadosPedido = ['en_espera', 'pendiente', 'en_prep', 'listo', 'entregado'];
  const estadosProductos = ['en_espera', 'pendiente', 'en_prep', 'listo', 'entregado'];

  // Agregar estos estados al inicio del componente (cerca de la línea 20)
  const [mostrarModalNotas, setMostrarModalNotas] = useState(false);
  const [productoParaNotas, setProductoParaNotas] = useState(null);
  const [notaProducto, setNotaProducto] = useState('');
  const [reimprimiendo, setReimprimiendo] = useState(false);
  const [imprimiendoComprobante, setImprimiendoComprobante] = useState(false);

  const esEstadoPagado = pedido?.estado === 'pagado' || (typeof pedido?.estado === 'string' && pedido?.estado.endsWith('_pagado'));

  const handleImprimirComprobante = async () => {
    if (!pedido) return;
    setImprimiendoComprobante(true);
    try {
      const pisoMesa = pedido?.piso ?? pedido?.numero_piso ?? pedido?.piso_mesa ?? auth?.user?.piso_asignado ?? 1;
      const mesaNumero = pedido?.numero ?? pedido?.numero_mesa;
      const mesaFormateada = mesaNumero 
        ? (String(mesaNumero).toLowerCase().startsWith('mesa') ? String(mesaNumero) : `Mesa ${mesaNumero}`)
        : (pedido?.id_mesa === 910100 ? 'DIRECTO' : (pedido?.mesa || `Mesa ${pedido?.id_mesa || ''}`));

      const itemsComprobante = (detalles || []).filter(d => d.estado !== 'cancelado').map(d => ({
        nombre: d.nombre_producto || d.producto || d.nombre || 'Producto',
        cantidad: d.cantidad || 1,
        precio: d.precio_unitario || d.precio || 0,
        subtotal: (d.cantidad || 1) * (d.precio_unitario || d.precio || 0)
      }));

      const datosComprobante = {
        pedidoId: pedido.id || parseInt(pedidoId),
        id: pedido.id || parseInt(pedidoId),
        mesa: mesaFormateada,
        piso: pisoMesa,
        cajero: auth?.user?.nombre || auth?.user?.usuario || pedido.mesero || 'Cajero',
        fecha: new Date().toLocaleDateString('es-PE'),
        hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
        items: itemsComprobante,
        total: pedido.total || 0,
        estado: pedido.estado,
        esPagado: true,
        metodoPago: pedido.metodo_pago || 'EFECTIVO'
      };

      const resClient = await clientPrinterService.imprimirComprobanteCliente(datosComprobante);

      if (resClient.success) {
        mostrarMensajeExito('Comprobante impreso correctamente');
      } else if (resClient.queued) {
        mostrarMensajeExito('⚠️ Impresora offline. Comprobante guardado en cola para impresión.');
      } else {
        mostrarMensajeExito('Proceso de impresión de comprobante finalizado');
      }
    } catch (err) {
      console.error('Error al imprimir comprobante:', err);
      mostrarMensajeError(`Error al imprimir comprobante: ${err.message}`);
    } finally {
      setImprimiendoComprobante(false);
    }
  };

  const handleReimprimirComanda = async () => {
    if (!pedido) return;
    setReimprimiendo(true);
    try {
      const cantSeleccionados = Object.keys(productosSeleccionados).filter(key => productosSeleccionados[key]).length;
      const detallesAImprimir = (detalles || []).filter(d => d.estado !== 'cancelado' && (cantSeleccionados > 0 ? productosSeleccionados[d.id] : true));

      const pisoMesa = pedido?.piso ?? pedido?.numero_piso ?? pedido?.piso_mesa ?? auth?.user?.piso_asignado ?? 1;
      const mesaNumero = pedido?.numero ?? pedido?.numero_mesa;
      const mesaFormateada = mesaNumero 
        ? (String(mesaNumero).toLowerCase().startsWith('mesa') ? String(mesaNumero) : `Mesa ${mesaNumero}`)
        : (pedido?.id_mesa === 910100 ? 'DIRECTO' : (pedido?.mesa || `Mesa ${pedido?.id_mesa || ''}`));

      const itemsComanda = detallesAImprimir.map(d => ({
        nombre: d.nombre_producto || d.producto || d.nombre || 'Producto',
        cantidad: d.cantidad || 1,
        notas: d.notas || '',
        categoria: d.categoria_nombre || d.categoria || d.nombre_categoria || d.tiempo || 'GENERAL'
      }));

      const datosComanda = {
        ordenId: pedido.id || parseInt(pedidoId),
        id: pedido.id || parseInt(pedidoId),
        mesa: mesaFormateada,
        piso: pisoMesa,
        mozo: pedido.mesero || pedido.usuario_nombre || auth?.user?.nombre || auth?.user?.usuario || 'Mozo',
        fecha: new Date().toLocaleDateString('es-PE'),
        hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
        items: itemsComanda,
        notaGeneral: pedido.observaciones || ''
      };

      const resClient = await clientPrinterService.imprimirComandaCliente(datosComanda, 'NUEVA');

      if (resClient.success) {
        mostrarMensajeExito('🖨️ Comanda reimpresa correctamente en el cliente');
      } else if (resClient.queued) {
        mostrarMensajeExito('⚠️ Impresora offline. Comanda en cola para reimpresión automática.');
      } else {
        mostrarMensajeExito('🖨️ Proceso de reimpresión finalizado');
      }
    } catch (err) {
      console.error('Error al reimprimir comanda:', err);
      mostrarMensajeError(`Error de red al reimprimir comanda: ${err.message}`);
      alert(`⚠️ Error de red al reimprimir comanda: ${err.message}`);
    } finally {
      setReimprimiendo(false);
    }
  };

  const esPedidoDirecto = () => {
    return pedido?.id_mesa === 910100;
  };

  // Agregar esta función después de las otras funciones existentes (antes del return)
  const iniciarEdicionNotas = (producto) => {
    setProductoParaNotas(producto);
    setNotaProducto(producto.notas || '');
    setMostrarModalNotas(true);
  };

  // Agregar esta función para guardar las notas
  const guardarNotasProducto = async () => {
    try {
      if (!productoParaNotas) return;

      const response = await fetch(getApiUrl(`/pedidos/${pedidoId}/detalles/${productoParaNotas.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({
          notas: notaProducto
        })
      });

      if (!response.ok) {
        throw new Error('Error al guardar la nota del producto');
      }

      // Actualizar datos locales
      const nuevosDetalles = detalles.map(detalle =>
        detalle.id === productoParaNotas.id ? { ...detalle, notas: notaProducto } : detalle
      );
      setDetalles(nuevosDetalles);

      setMostrarModalNotas(false);
      setProductoParaNotas(null);
      mostrarMensajeExito('Nota guardada correctamente');
    } catch (err) {
      setError(err.message);
    }
  };

  // función para obtener el estado correspondiente para pedidos directos
  const getEstadoEquivalentePedidoDirecto = (estado) => {
    // Si ya tiene _pagado, lo dejamos igual
    if (estado.includes('_pagado')) {
      return estado;
    }

    // Convertimos el estado normal a su equivalente para pedidos directos
    return `${estado}_pagado`;
  };

  // Función para obtener los estados siguientes permitidos
  const getEstadosSiguientes = (estadoActual) => {
    // Quitar sufijo _pagado para encontrar el estado base
    const estadoBase = estadoActual.replace('_pagado', '');
    const indice = estadosPedido.indexOf(estadoBase);

    if (indice === -1) return [];

    // Obtenemos los estados siguientes base
    const siguientesBase = estadosPedido.slice(indice + 1);

    // Si es pedido directo, convertimos a estados con sufijo _pagado
    if (esPedidoDirecto()) {
      return siguientesBase.map(estado => `${estado}_pagado`);
    }

    return siguientesBase;
  };

  // Agregar después de la declaración de los estados existentes (línea ~29)
  const [productosSeleccionados, setProductosSeleccionados] = useState({});
  const [haySeleccionados, setHaySeleccionados] = useState(false);

  // Función para verificar si todos los productos tienen el mismo estado
  const todosMismoEstado = () => {
    // Filtramos detalles que no están cancelados ni pagados
    const detallesActivos = detalles.filter(detalle =>
      !detalle.estado.includes('cancelado') &&
      (esPedidoDirecto() ? !detalle.estado.includes('pagado') || detalle.estado.includes('_pagado') : !detalle.estado.includes('pagado'))
    );

    if (detallesActivos.length === 0) return true;

    // Obtenemos el prefijo del estado (sin _pagado si lo tiene)
    const obtenerPrefijoPrincipal = (estado) => {
      return estado.replace('_pagado', '');
    };

    // Verificamos si todos tienen el mismo prefijo de estado principal
    const primerEstadoPrincipal = obtenerPrefijoPrincipal(detallesActivos[0].estado);
    return detallesActivos.every(detalle =>
      obtenerPrefijoPrincipal(detalle.estado) === primerEstadoPrincipal
    );
  };

  // Función para verificar si hay productos pagados (estado 'pagado' o con sufijo '_pagado')
  const hayProductosPagados = () => {
    return detalles.some(detalle =>
      detalle.estado.includes('pagado')
    );
  };

  // Función para verificar si todos los productos están entregados
  const todosEntregados = () => {
    // Filtramos detalles que no están cancelados
    const detallesActivos = detalles.filter(detalle => detalle.estado !== 'cancelado');

    if (detallesActivos.length === 0) return false;

    // Verificamos si todos están entregados
    return detallesActivos.every(detalle => detalle.estado === 'entregado');
  };

  // Función para obtener el estado común de los productos activos
  const getEstadoComun = () => {
    const detallesActivos = detalles.filter(detalle => !detalle.estado.includes('cancelado'));

    if (detallesActivos.length === 0) return null;

    // Obtenemos el prefijo del estado
    const obtenerPrefijoPrincipal = (estado) => {
      return estado.replace('_pagado', '');
    };

    const primerEstadoPrincipal = obtenerPrefijoPrincipal(detallesActivos[0].estado);

    if (detallesActivos.every(detalle =>
      obtenerPrefijoPrincipal(detalle.estado) === primerEstadoPrincipal)) {
      // Si todos tienen el mismo prefijo, devolvemos el estado base del primero
      // Para pedidos directos, aseguramos que usemos el estado con sufijo si corresponde
      if (esPedidoDirecto() && !detallesActivos[0].estado.includes('_pagado')) {
        return `${primerEstadoPrincipal}_pagado`;
      }
      return detallesActivos[0].estado;
    }

    return null;
  };

  // Función para actualización silenciosa envuelta en useCallback
  const fetchPedidoSilencioso = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        headers: { 'Authorization': `Bearer ${auth?.token}` }
      });

      if (!response.ok) return;

      const data = await response.json();

      setPedido(prevPedido => {
        if (JSON.stringify(data) !== JSON.stringify(prevPedido)) {
          return data;
        }
        return prevPedido;
      });

      if (data.detalles) {
        setDetalles(prevDetalles => {
          if (JSON.stringify(data.detalles) !== JSON.stringify(prevDetalles)) {
            return data.detalles;
          }
          return prevDetalles;
        });
      }
    } catch (err) {
      console.error('Error en actualización silenciosa:', err);
    }
  }, [pedidoId, auth?.token]);

  const fetchPedidoDetalle = useCallback(async () => {
    try {
      setLoading(true);

      const response = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar los detalles del pedido');
      }

      const data = await response.json();
      setPedido(data);

      // Cargar detalles del pedido
      if (data.detalles) {
        setDetalles(data.detalles);
      } else {
        const responseDetalles = await fetch(getApiUrl(`/pedidos/${pedidoId}/detalles`), {
          headers: {
            'Authorization': `Bearer ${auth?.token}`
          }
        });

        if (responseDetalles.ok) {
          const detallesData = await responseDetalles.json();
          setDetalles(detallesData);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error al cargar pedido:', err);
      setError(err.message || 'Error al cargar el pedido');
      setLoading(false);
    }
  }, [pedidoId, auth?.token]);

  useEffect(() => {
    // Carga inicial
    fetchPedidoDetalle();

    const handleSocketUpdate = (data) => {
      console.log('Evento de socket recibido en DetallePedidoComponent:', data);
      fetchPedidoSilencioso();
    };

    socket.on('cambioEstadoComanda', handleSocketUpdate);

    return () => {
      socket.off('cambioEstadoComanda', handleSocketUpdate);
    };
  }, [fetchPedidoDetalle, fetchPedidoSilencioso]);

  // Efecto para sincronizar el estado del pedido con el estado de los productos
  useEffect(() => {
    if (detalles.length > 0 && pedido) {
      const estadoComun = getEstadoComun();
      if (estadoComun && estadoComun !== pedido.estado) {
        setPedido(prevPedido => ({ ...prevPedido, estado: estadoComun }));
      }
    }
  }, [detalles, pedido?.id]);

  const formatearFecha = (timestamp) => {
    if (!timestamp) return 'N/A';
    const fecha = new Date(timestamp);
    return fecha.toLocaleString();
  };

  // Función para actualizar el estado de un único detalle de producto
  const cambiarEstadoDetalle = async (idDetalle, nuevoEstado) => {
    try {
      // Si es un pedido directo, usamos el estado con sufijo _pagado
      const estadoAActualizar = esPedidoDirecto()
        ? getEstadoEquivalentePedidoDirecto(nuevoEstado)
        : nuevoEstado;

      console.log(`Actualizando detalle ${idDetalle} a estado: ${estadoAActualizar}`);

      const response = await fetch(getApiUrl(`/pedidos/${pedidoId}/detalles/${idDetalle}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({ estado: estadoAActualizar })
      });

      if (!response.ok) {
        throw new Error('Error al actualizar el estado del producto');
      }

      // Obtener el detalle actualizado desde la respuesta
      const detalleActualizado = await response.json();

      // Actualizar datos locales con todos los campos del detalle, incluidos los timestamps
      const nuevosDetalles = detalles.map(detalle =>
        detalle.id === idDetalle ? { ...detalle, ...detalleActualizado } : detalle
      );
      setDetalles(nuevosDetalles);

      // Mostrar mensaje de éxito
      mostrarMensajeExito(`Estado del producto actualizado a: ${estadoAActualizar.replace('_', ' ')}`);

      // Resto del código para actualizar el estado del pedido...
      // ...

      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // Función para cancelar un producto específico
  const cancelarProducto = async () => {
    try {
      if (!productoACancelar) return;

      const response = await fetch(getApiUrl(`/pedidos/${pedidoId}/detalles/${productoACancelar.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({
          estado: 'cancelado',
          notas: motivoCancelacionProducto, // Usar el campo notas existente para el motivo
          id_caja: caja?.id
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'No se pudo cancelar el producto. Intenta nuevamente.');
      }

      // Imprimir comanda de anulación en cliente para el plato anulado
      try {
        const pisoMesa = pedido?.piso ?? pedido?.numero_piso ?? pedido?.piso_mesa ?? auth?.user?.piso_asignado ?? 1;
        const mesaNumero = pedido?.numero ?? pedido?.numero_mesa;
        const mesaFormateada = mesaNumero 
          ? (String(mesaNumero).toLowerCase().startsWith('mesa') ? String(mesaNumero) : `Mesa ${mesaNumero}`)
          : (pedido?.id_mesa === 910100 ? 'DIRECTO' : (pedido?.mesa || `Mesa ${pedido?.id_mesa || ''}`));

        const datosAnulacion = {
          mesa: mesaFormateada,
          piso: pisoMesa,
          mozo: auth?.user?.nombre || auth?.user?.usuario || 'Mozo',
          hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
          motivo: motivoCancelacionProducto || 'Cancelación de plato',
          items: [{
            nombre: productoACancelar.nombre_producto || productoACancelar.producto || productoACancelar.nombre || 'Producto',
            cantidad: productoACancelar.cantidad || 1,
            notas: productoACancelar.notas || ''
          }]
        };

        await clientPrinterService.imprimirAnulacionCliente(datosAnulacion);
      } catch (printErr) {
        console.error('Error al emitir comanda de anulación de producto:', printErr);
      }

      // Actualizar datos locales
      const nuevosDetalles = detalles.map(detalle =>
        detalle.id === productoACancelar.id ? { ...detalle, estado: 'cancelado' } : detalle
      );
      setDetalles(nuevosDetalles);

      // Verificar si todos los productos están cancelados
      const todosProductosCancelados = nuevosDetalles.every(detalle => detalle.estado === 'cancelado');

      if (todosProductosCancelados) {
        // Actualizar el estado del pedido localmente
        setPedido({ ...pedido, estado: 'cancelado', observaciones: 'Pedido cancelado - Todos los productos fueron cancelados' });
        mostrarMensajeExito('Todos los productos han sido cancelados. El pedido ha sido cancelado automáticamente.');
      } else {
        mostrarMensajeExito('Producto cancelado correctamente');
      }

      setMostrarModalCancelarProducto(false);
      setProductoACancelar(null);
      setMotivoCancelacionProducto('');

      // Actualizar también el total del pedido
      const productoCancelado = detalles.find(d => d.id === productoACancelar.id);
      if (productoCancelado) {
        const nuevoTotal = pedido.total - (productoCancelado.precio_unitario * productoCancelado.cantidad);
        setPedido({ ...pedido, total: Math.max(0, nuevoTotal) });
      }

      // Después de cancelar el producto, recargar todos los datos del pedido
      await fetchPedidoDetalle();

      setMostrarModalCancelarProducto(false);
      setProductoACancelar(null);
      setMotivoCancelacionProducto('');

      mostrarMensajeExito('Producto cancelado correctamente');
    } catch (err) {
      setMostrarModalCancelarProducto(false);
      mostrarMensajeError(err.message);
    }
  };

  // Función para iniciar el proceso de cancelación de un producto
  const iniciarCancelacionProducto = (producto) => {
    setProductoACancelar(producto);
    setMostrarModalCancelarProducto(true);
  };

  // Función para actualizar todos los detalles de productos (excepto los cancelados ni pagados)
  const actualizarTodosDetalles = async (nuevoEstado) => {
    try {
      // Filtramos los detalles que no están cancelados
      // Para pedidos directos, permitimos estados con '_pagado'
      const detallesActualizables = detalles.filter(detalle => {
        // Siempre excluir cancelados
        if (detalle.estado.includes('cancelado')) return false;

        // Para pedidos directos, solo excluir el estado 'pagado' pero permitir estados con '_pagado'
        if (esPedidoDirecto()) {
          return detalle.estado !== 'pagado';
        } else {
          // Para pedidos normales, excluir cualquier estado con 'pagado'
          return !detalle.estado.includes('pagado');
        }
      });

      // Realizamos las actualizaciones en paralelo
      const actualizaciones = detallesActualizables.map(detalle => {
        return cambiarEstadoDetalle(detalle.id, nuevoEstado);
      });

      // Esperamos a que todas las actualizaciones se completen
      await Promise.all(actualizaciones);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  // Función modificada para actualizar tanto el pedido como todos sus detalles
  const cambiarEstadoPedido = async (nuevoEstado) => {
    try {
      setActualizandoEstado(true); // Indicar que está actualizando

      const estadoAActualizar = esPedidoDirecto()
        ? getEstadoEquivalentePedidoDirecto(nuevoEstado)
        : nuevoEstado;

      // setMensajeExito(`Actualizando pedido y productos a estado: ${estadoAActualizar.replace('_', ' ')}...`);

      // Primero actualizamos el estado de todos los productos
      if (['pendiente', 'en_prep', 'listo', 'entregado'].includes(nuevoEstado)) {
        const resultado = await actualizarTodosDetalles(nuevoEstado);
        if (!resultado) {
          throw new Error('Error al actualizar los productos del pedido');
        }
      }

      // Luego actualizamos el estado del pedido
      const response = await fetch(getApiUrl(`/pedidos/${pedidoId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({ estado: estadoAActualizar })
      });

      if (!response.ok) {
        throw new Error('Error al actualizar el estado del pedido');
      }

      // Recargar los datos actualizados desde el servidor
      await fetchPedidoDetalle();

      mostrarMensajeExito(`Estado del pedido actualizado a: ${estadoAActualizar.replace('_', ' ')} (productos no cancelados)`);
    } catch (err) {
      setError(err.message);
    } finally {
      setActualizandoEstado(false); // Finalizar actualización pase lo que pase
    }
  };

  const cancelarPedido = async () => {
    try {
      // Capturar los ítems no cancelados antes de enviar la petición de cancelación
      const itemsACancelar = (detalles || [])
        .filter(d => !d.estado?.includes('cancelado'))
        .map(d => ({
          nombre: d.nombre_producto || d.producto || d.nombre || 'Producto',
          cantidad: d.cantidad || 1,
          notas: d.notas || ''
        }));

      const response = await fetch(getApiUrl(`/canc/pedidos/${pedidoId}/cancelar`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth?.token}`
        },
        body: JSON.stringify({ motivo: motivoCancelacion })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'No se pudo cancelar el pedido. Intenta nuevamente.');
      }

      const data = await response.json();

      // Imprimir comanda de anulación en cliente para los platos del pedido cancelado
      if (itemsACancelar.length > 0) {
        try {
          const pisoMesa = pedido?.piso ?? pedido?.numero_piso ?? pedido?.piso_mesa ?? auth?.user?.piso_asignado ?? 1;
          const mesaNumero = pedido?.numero ?? pedido?.numero_mesa;
          const mesaFormateada = mesaNumero 
            ? (String(mesaNumero).toLowerCase().startsWith('mesa') ? String(mesaNumero) : `Mesa ${mesaNumero}`)
            : (pedido?.id_mesa === 910100 ? 'DIRECTO' : (pedido?.mesa || `Mesa ${pedido?.id_mesa || ''}`));

          const datosAnulacion = {
            mesa: mesaFormateada,
            piso: pisoMesa,
            mozo: auth?.user?.nombre || auth?.user?.usuario || 'Mozo',
            hora: new Date().toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' }),
            motivo: motivoCancelacion || 'Anulación de pedido completo',
            items: itemsACancelar
          };

          await clientPrinterService.imprimirAnulacionCliente(datosAnulacion);
        } catch (printErr) {
          console.error('Error al emitir comanda de anulación de pedido:', printErr);
        }
      }

      // Actualizar datos locales con la respuesta real del servidor
      setPedido(data);

      // Recargar todos los detalles para reflejar el estado real después de la cancelación
      await fetchPedidoDetalle();

      setMostrarModalCancelar(false);

      const mensaje = data.estado === 'cancelado'
        ? 'Pedido cancelado correctamente'
        : 'Los productos pendientes han sido cancelados. Los productos pagados se mantienen.';

      mostrarMensajeExito(mensaje);
    } catch (err) {
      setMostrarModalCancelar(false);
      mostrarMensajeError(err.message);
    }
  };

  const mostrarMensajeExito = (mensaje) => {
    setMensajeExito(mensaje);
    setTimeout(() => setMensajeExito(''), 3000);
  };

  const mostrarMensajeError = (mensaje) => {
    setMensajeError(mensaje);
    setTimeout(() => setMensajeError(''), 5000);
  };

  const volverAMesa = () => {
    navigate(`/mesa/${pedido?.id_mesa}/pedidos`);
  };

  // Función para navegar al componente de pago para un producto específico
  const irAPagarProducto = (detalleId) => {
    navigate(`/pago/${pedidoId}/producto/${detalleId}`);
  };

  // Función para navegar al componente de pago del pedido completo
  const irAPagarPedidoCompleto = () => {
    navigate(`/pago/${pedidoId}`);
  };

  // Función para calcular el total pagado
  const calcularTotalPagado = () => {
    return pedido?.total_pago || 0;
  };

  // Función para calcular el saldo pendiente
  const calcularSaldoPendiente = () => {
    if (!pedido) return 0;
    return pedido.total - calcularTotalPagado();
  };

  // Función para verificar si hay productos sin pagar
  const hayProductosSinPagar = () => {
    return detalles.some(detalle =>
      detalle.estado !== 'pagado' && detalle.estado !== 'cancelado' && detalle.estado !== 'pendiente_pagado' && detalle.estado !== 'en_prep_pagado' && detalle.estado !== 'listo_pagado' && detalle.estado !== 'entregado_pagado'
    );
  };

  if (loading) {
    return <div className="loading">Cargando detalles del pedido...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">Error: {error}</div>
        <button onClick={() => navigate('/mesas')} className="btn-volver">
          Volver a Mesas
        </button>
      </div>
    );
  }

  if (!pedido) {
    return <div className="error">No se encontró el pedido</div>;
  }

  // restric estados
  const puedeModificar = ['pendiente', 'en_prep', 'listo', 'entregado'].includes(pedido.estado.replace('_pagado', ''));

  const localPrinter = clientPrinterService.getLocalPrinter(auth?.user?.id);
  const esModoVirtual = localPrinter?.tipo_conexion === 'virtual';

  const esEstadoPagadoOCancelado = !pedido || pedido.estado === 'pagado' || pedido.estado === 'entregado_pagado' || pedido.estado === 'cancelado' || (typeof pedido.estado === 'string' && pedido.estado.endsWith('_pagado'));

  // Productos que aún pueden cancelarse (no pagados, no cancelados)
  const hayProductosCancelables = detalles.some(d =>
    !d.estado.includes('pagado') && d.estado !== 'cancelado'
  );
  // MOZO/CAJERO: verificar directamente los detalles (no pedido.estado, que puede haber sido
  // sobrescrito con sufijo _pagado por getEstadoComun() aunque los productos sean en_espera)
  const hayProductosEnEspera = detalles.some(d => d.estado === 'en_espera');

  // ENCARGADO: puede cancelar si queda algún producto no pagado/cancelado
  // MOZO/CAJERO: puede cancelar solo si hay productos en en_espera exacto
  // Además, si ya hay productos pagados, bloqueamos la cancelación general del pedido.
  const puedeCancelar = (pedido.estado !== 'pagado' && pedido.estado !== 'cancelado' && !hayProductosPagados()) && (
    auth.user.rol === 'ENCARGADO'
      ? hayProductosCancelables
      : hayProductosEnEspera
  );

  // Función para manejar la selección/deselección de productos
  const toggleSeleccionProducto = (detalleId) => {
    const nuevaSeleccion = {
      ...productosSeleccionados,
      [detalleId]: !productosSeleccionados[detalleId]
    };
    setProductosSeleccionados(nuevaSeleccion);

    // Verificar si hay al menos un producto seleccionado
    const algunoSeleccionado = Object.values(nuevaSeleccion).some(seleccionado => seleccionado);
    setHaySeleccionados(algunoSeleccionado);
  };

  // Función para seleccionar/deseleccionar todos los productos disponibles para pago
  const toggleSeleccionarTodos = () => {
    const productosDisponibles = detalles.filter(d => d.estado !== 'cancelado');

    // Si todos están seleccionados, deseleccionar todos
    const todoSeleccionado = productosDisponibles.every(
      d => productosSeleccionados[d.id]
    );

    const nuevaSeleccion = {};
    productosDisponibles.forEach(d => {
      nuevaSeleccion[d.id] = !todoSeleccionado;
    });

    setProductosSeleccionados(nuevaSeleccion);
    setHaySeleccionados(!todoSeleccionado && productosDisponibles.length > 0);
  };

  // Función para navegar al pago de productos seleccionados
  const irAPagarProductosSeleccionados = () => {
    // Crear un arreglo con los IDs de los productos seleccionados
    const idsSeleccionados = Object.keys(productosSeleccionados)
      .filter(key => productosSeleccionados[key])
      .map(id => parseInt(id));

    if (idsSeleccionados.length === 0) {
      setError('Debes seleccionar al menos un producto para pagar');
      return;
    }

    // Usar sessionStorage para pasar los IDs seleccionados
    sessionStorage.setItem('productosSeleccionados', JSON.stringify(idsSeleccionados));
    navigate(`/pago/${pedidoId}/seleccionados`);
  };

  const cantSeleccionados = Object.keys(productosSeleccionados).filter(key => productosSeleccionados[key]).length;

  return (
    <div className="detalle-pedido-container">
      {mensajeExito && (
        <div className="mensaje-exito">
          {mensajeExito}
        </div>
      )}

      {mensajeError && (
        <div className="mensaje-error-inline">
          {mensajeError}
        </div>
      )}

      <div className="detalle-pedido-header">
        <h2>Detalle de Pedido #{pedido.id}</h2>
        <div className="botones-navegacion">
          {origenCaja && (
            <button onClick={() => navigate('/caja')} className="btn-volver">
              Volver a Caja
            </button>
          )}
          {esPedidoDirecto() ? (
            <button onClick={() => navigate('/pedidos-directos')} className="btn-volver">
              Pedidos Directos
            </button>
          ) : (
            <>
              <button onClick={volverAMesa} className="btn-volver">
                Volver a Mesa
              </button>
              {!origenCaja && (
                <button onClick={() => navigate('/mesas')} className="btn-mesas">
                  Todas las Mesas
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-pedido">
        <div className="pedido-meta">
          <h3>Información General</h3>
          <p><strong>Mesa/Origen:</strong> {esPedidoDirecto() ? 'Pedido Directo (Barra / Para Llevar)' : (pedido.numero_mesa || pedido.id_mesa)}</p>
          <p><strong>Estado:</strong> {pedido.estado.replace(/_/g, ' ')}</p>
          <p><strong>Total:</strong> S/. {Number(pedido.total).toFixed(2)}</p>

          {/* Mostrar información de pagos si existe */}
          {pedido.total_pago > 0 && (
            <div className="info-pagos">
              <p><strong>Pagado:</strong> S/. {calcularTotalPagado().toFixed(2)}</p>
              <p><strong>Pendiente:</strong> S/. {calcularSaldoPendiente().toFixed(2)}</p>
              {pedido.metodo_pago && (
                <p><strong>Método de pago:</strong> {pedido.metodo_pago}</p>
              )}
            </div>
          )}

          <p><strong>Inicio:</strong> {formatearFecha(pedido.tiempo_inicio)}</p>
          {pedido.tiempo_entrega && (
            <p><strong>Entrega:</strong> {formatearFecha(pedido.tiempo_entrega)}</p>
          )}
          {pedido.observaciones && (
            <p><strong>Observaciones:</strong> {pedido.observaciones}</p>
          )}
        </div>

        {puedeModificar && (
          <div className="cambiar-estado">
            <h3>Cambiar Estado del Pedido</h3>
            {actualizandoEstado ? (
              <div className="actualizando-estado">
                <p>Actualizando estado del pedido...</p>
              </div>
            ) : !todosMismoEstado() ? (
              <div className="alerta-estados">
                <p>No se puede cambiar el estado del pedido porque los productos tienen estados diferentes.</p>
                <p>Todos los productos deben tener el mismo estado para actualizar el pedido completo.</p>
              </div>
            ) : (
              <div className="opciones-estado">
                {getEstadosSiguientes(pedido.estado).map(estado => (
                  <button
                    key={estado}
                    onClick={() => cambiarEstadoPedido(estado.replace('_pagado', ''))}
                    className="btn-estado"
                  >
                    {estado.replace('_', ' ')}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modificar la sección de acciones-pedido (línea 471 aproximadamente) */}
        <div className="acciones-pedido">
          {/* Botón para agregar productos si se puede modificar el pedido */}
          {puedeModificar && pedido.estado !== 'cancelado' && !esPedidoDirecto() && (
            <button
              onClick={() => navigate(`/pedido/${pedidoId}/agregar-productos`)}
              className="btn-agregar-productos"
            >
              + Agregar Productos
            </button>
          )}

          {/* Botón para pagar productos seleccionados */}
          {hayProductosSinPagar() && pedido.estado !== 'cancelado' && auth?.user?.rol !== 'MOZO' && auth?.role !== 'MOZO' && (
            <>
              <button
                onClick={irAPagarProductosSeleccionados}
                className="btn-pagar-seleccionados"
                disabled={!haySeleccionados}
              >
                Pagar Seleccionados
              </button>

              {/* Botón para pagar todo el pedido */}
              <button
                onClick={irAPagarPedidoCompleto}
                className="btn-pagar-pedido"
              >
                Pagar Pedido
              </button>
            </>
          )}

          {/* Botón Imprimir Comprobante cuando está pagado */}
          {esEstadoPagado && (
            <button
              onClick={handleImprimirComprobante}
              disabled={imprimiendoComprobante}
              className="btn-imprimir-comprobante"
              style={{
                background: '#10b981',
                color: '#fff',
                border: 'none',
                padding: '0.65rem 1.2rem',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: imprimiendoComprobante ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.9rem'
              }}
            >
              {imprimiendoComprobante ? '⏳ Imprimiendo Comprobante...' : 'Imprimir Comprobante'}
            </button>
          )}

          {/* Botón Reimprimir Comanda */}
          {!esEstadoPagadoOCancelado && !esModoVirtual && (
            <button
              onClick={handleReimprimirComanda}
              disabled={reimprimiendo}
              className="btn-reimprimir-comanda"
              style={{
                background: '#6366f1',
                color: '#fff',
                border: 'none',
                padding: '0.65rem 1.2rem',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: reimprimiendo ? 'wait' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.9rem'
              }}
            >
              {reimprimiendo ? '⏳ Reimprimiendo...' : (cantSeleccionados > 0 ? `Reimprimir (${cantSeleccionados} Seleccionado${cantSeleccionados !== 1 ? 's' : ''})` : 'Reimprimir Todo el Pedido')}
            </button>
          )}

          {puedeCancelar && (
            <button
              onClick={() => setMostrarModalCancelar(true)}
              className="btn-cancelar"
            >
              Cancelar Pedido
            </button>
          )}


          {/* {puedeCancelar && todosMismoEstado() && hayProductosPagados() && (
            <div className="alerta-mensaje">
              <p>No se puede cancelar el pedido porque hay productos pagados</p>
            </div>
          )} */}
        </div>
      </div>

      <div className="productos-pedido">
        <div className="productos-pedido-header">
          <h3>Productos</h3>
          <span className="contador-productos">{detalles.length} item{detalles.length !== 1 ? 's' : ''}</span>
        </div>
        <table className="tabla-productos">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={toggleSeleccionarTodos}
                  checked={
                    detalles.filter(d => d.estado !== 'cancelado').length > 0 &&
                    detalles.filter(d => d.estado !== 'cancelado')
                      .every(d => productosSeleccionados[d.id])
                  }
                />
              </th>
              <th>Producto</th>
              <th>Cantidad</th>
              <th>Precio</th>
              <th>Subtotal</th>
              <th className="desktop-only text-center">Estado</th>
              <th>Tiempo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {detalles.map(detalle => (
              <tr key={detalle.id} className={detalle.estado === 'cancelado' ? 'producto-cancelado' : ''}>
                <td>
                  {detalle.estado !== 'cancelado' && (
                    <input
                      type="checkbox"
                      checked={!!productosSeleccionados[detalle.id]}
                      onChange={() => toggleSeleccionProducto(detalle.id)}
                    />
                  )}
                </td>
                <td>{detalle.nombre_producto || `Producto ${detalle.id_producto}`}</td>
                <td>{detalle.cantidad}</td>
                <td>S/. {detalle.precio_unitario}</td>
                <td>S/. {(detalle.cantidad * detalle.precio_unitario).toFixed(2)}</td>
                <td className="desktop-only text-center">
                  {detalle.estado.replace(/_/g, ' ')}
                  {detalle.estado === 'pagado' && detalle.metodo_pago_producto && (
                    <span className="metodo-pago-producto"> ({detalle.metodo_pago_producto})</span>
                  )}
                </td>
                <td>
                  {/* Mostrar la marca de tiempo según el estado */}
                  <div className="tiempos-producto">
                    <p><strong>Pedido:</strong> {formatearFecha(detalle.tiempo_pedido)}</p>
                    {detalle.estado === 'en_prep' || detalle.estado === 'listo' || detalle.estado === 'entregado' ?
                      <p><strong>En preparación:</strong> {formatearFecha(detalle.tiempo_preparacion)}</p> : null}
                    {detalle.estado === 'listo' || detalle.estado === 'entregado' ?
                      <p><strong>Listo:</strong> {formatearFecha(detalle.tiempo_listo)}</p> : null}
                    {detalle.estado === 'entregado' ?
                      <p><strong>Entregado:</strong> {formatearFecha(detalle.tiempo_entregado)}</p> : null}
                  </div>
                </td>
                <td className="acciones-col">
                  <div className="acciones-producto">
                    {puedeModificar && detalle.estado !== 'cancelado' && detalle.estado !== 'pagado' &&
                      /* Para pedidos directos, no mostrar cambio de estado si el producto aún está en espera (sin pago) */
                      !(esPedidoDirecto() && detalle.estado === 'en_espera') && (
                      <>
                        {/* Vista Escritorio: Botones directos */}
                        <div className="botones-estado-producto desktop-only">
                          {getEstadosSiguientes(detalle.estado.replace('_pagado', '')).map(estado => (
                            estado !== 'pagado' && (
                              <button
                                key={estado}
                                onClick={() => cambiarEstadoDetalle(detalle.id, estado)}
                                className={`btn-estado-producto estado-${estado.replace('_pagado', '')}`}
                              >
                                {estado.replace('_', ' ')}
                              </button>
                            )
                          ))}
                        </div>

                        {/* Vista Móvil: Botón principal y Notas juntos */}
                        <div className="botones-estado-producto mobile-only">
                          <div className="fila-principal-mobile">
                            <button
                              className={`btn-estado-actual-mobile estado-${detalle.estado.replace('_pagado', '')}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuEstadoId(menuEstadoId === detalle.id ? null : detalle.id);
                              }}
                            >
                              <span>{detalle.estado.replace('_', ' ')} ▾</span>
                            </button>

                            <button
                              onClick={() => iniciarEdicionNotas(detalle)}
                              className="btn-notas-producto-mobile"
                              title="Agregar/Editar nota"
                            >
                              <span role="img" aria-label="nota">📝</span>
                            </button>
                          </div>

                        </div>
                      </>
                    )}

                    <div className="fila-acciones-secundarias">
                      {/* Botón para pagar un producto individual */}
                      {detalle.estado !== 'pagado' && detalle.estado !== 'cancelado' && detalle.estado !== 'pendiente_pagado' && detalle.estado !== 'en_prep_pagado' && detalle.estado !== 'listo_pagado' && detalle.estado !== 'entregado_pagado' && auth?.user?.rol !== 'MOZO' && auth?.role !== 'MOZO' && (
                        <button
                          onClick={() => irAPagarProducto(detalle.id)}
                          className="btn-pagar-producto"
                        >
                          💳 Pagar
                        </button>
                      )}

                      {/* Solo mostrar el botón de cancelar si el estado del producto es cancelable y no fue pagado */}
                      {(!detalle.estado.includes('pagado') &&
                        (auth.user.rol === 'ENCARGADO'
                          ? ['en_espera', 'pendiente', 'en_prep', 'listo', 'entregado'].includes(detalle.estado)
                          : (esPedidoDirecto()
                            ? detalle.estado === 'en_espera'
                            : ['en_espera', 'pendiente'].includes(detalle.estado)
                          )
                        )
                      ) && (
                          <button
                            onClick={() => iniciarCancelacionProducto(detalle)}
                            className="btn-cancelar-producto"
                          >
                            Cancelar
                          </button>
                        )}

                      <button
                        onClick={() => iniciarEdicionNotas(detalle)}
                        className="btn-notas-producto desktop-only"
                        title="Agregar/Editar nota"
                      >
                        <span role="img" aria-label="nota">📝</span>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mostrarModalCancelar && (
        <div className="modal-cancelar">
          <div className="modal-content">
            <h3>Cancelar Pedido</h3>
            <p>¿Estás seguro de que deseas cancelar este pedido?</p>
            <textarea
              placeholder="Motivo de cancelación (obligatorio)"
              value={motivoCancelacion}
              onChange={(e) => setMotivoCancelacion(e.target.value)}
              className="input-motivo"
            />
            <div className="modal-buttons">
              <button onClick={() => setMostrarModalCancelar(false)} className="btn-cancelar-accion">
                No, Volver
              </button>
              <button
                onClick={cancelarPedido}
                className="btn-confirmar"
                disabled={!motivoCancelacion.trim()}
              >
                Sí, Cancelar Pedido
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalCancelarProducto && productoACancelar && (
        <div className="modal-cancelar">
          <div className="modal-content">
            <h3>Cancelar Producto</h3>
            <p>¿Estás seguro de que deseas cancelar el producto: <strong>{productoACancelar.nombre_producto || `Producto ${productoACancelar.id_producto}`}</strong>?</p>
            <textarea
              placeholder="Motivo de cancelación (obligatorio)"
              value={motivoCancelacionProducto}
              onChange={(e) => setMotivoCancelacionProducto(e.target.value)}
              className="input-motivo"
            />
            <div className="modal-buttons">
              <button
                onClick={() => {
                  setMostrarModalCancelarProducto(false);
                  setProductoACancelar(null);
                  setMotivoCancelacionProducto('');
                }}
                className="btn-cancelar-accion"
              >
                No, Volver
              </button>
              <button
                onClick={cancelarProducto}
                className="btn-confirmar"
                disabled={!motivoCancelacionProducto.trim()}
              >
                Sí, Cancelar Producto
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarModalNotas && productoParaNotas && (
        <div className="modal-notas">
          <div className="modal-content">
            <h3>Notas para {productoParaNotas.nombre_producto || `Producto ${productoParaNotas.id_producto}`}</h3>
            <textarea
              placeholder="Escriba una nota para este producto..."
              value={notaProducto}
              onChange={(e) => setNotaProducto(e.target.value)}
              className="input-nota"
              rows="4"
            />
            <div className="modal-buttons">
              <button
                onClick={() => {
                  setMostrarModalNotas(false);
                  setProductoParaNotas(null);
                }}
                className="btn-cancelar-accion"
              >
                Cancelar
              </button>
              <button
                onClick={guardarNotasProducto}
                className="btn-confirmar"
              >
                Guardar Nota
              </button>
            </div>
          </div>
        </div>
      )}

      {menuEstadoId && (() => {
        const detalleActivo = detalles.find(d => d.id === menuEstadoId);
        if(!detalleActivo) return null;
        return (
          <div className="modal-overlay-estados" onClick={() => setMenuEstadoId(null)}>
            <div className="modal-contenido-estados" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-estados">
                <h4>Cambiar Estado</h4>
                <p>{detalleActivo.nombre_producto || `Producto ${detalleActivo.id_producto}`}</p>
              </div>
              <div className="opciones-estados-lista">
                {getEstadosSiguientes(detalleActivo.estado.replace('_pagado', '')).map(estado => (
                  estado !== 'pagado' && (
                    <button
                      key={estado}
                      onClick={() => {
                        cambiarEstadoDetalle(detalleActivo.id, estado);
                        setMenuEstadoId(null);
                      }}
                      className={`btn-menu-estado estado-${estado.replace('_pagado', '')}`}
                    >
                      {estado.replace(/_/g, ' ')}
                    </button>
                  )
                ))}
              </div>
              <button className="btn-cerrar-modal-estados" onClick={() => setMenuEstadoId(null)}>
                Cancelar
              </button>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default DetallePedidoComponent;